import {
    filters as blankFilters,
    searchRequest as blankSearchRequest,
    searchResponse as blankSearchResponse,
} from '~src/utils/simpleTours/simple-tour-blank-states'
import {appInstance} from '@/utils/app-accessor'
import {runtimeStore} from '@/composables/store-accessor'
import type {Ref} from 'vue'
import {isAfter, isBefore, parseISO} from 'date-fns'
import {UTCToDate} from '@/utils/helpers'
import {useProductStore} from '~/composables/product-store'
import {salesTermsRsTransformer} from '@/utils/api-helpers'

export const useSimpleTourStore = defineStore('simpleTour', () => {
    const searchRequest: Ref = ref(blankSearchRequest())
    const searchResponse: Ref = ref(blankSearchResponse())
    const filters: Ref = ref(blankFilters())
    const sortFnName = ref('priceAsc')
    const groupOffersBy = ref('none')
    const searchExpirationTime: Ref = ref(null)
    const prepareBookRequest: Ref = ref({})
    const prepareBookResponse = ref({})

    const basket: Ref<any[]> = ref([])
    const bookingAdditionalOptions = ref({})

    function _getBasketItem(basket: Ref<any[]>, offerKey: any) {
        return (
            basket.value.find((item: {offerKey: any}) => item.offerKey === offerKey) || {
                searchRequest: blankSearchRequest(),
                product: {offers: [], info: {}},
                info: {},
            }
        )
    }

    function ADD_TO_BASKET({info, offerKey}: any) {
        basket.value = basket.value.filter(item => {
            const offer = item.product.offers.find((offer: {offerKey: any}) => offer.offerKey === item.offerKey)
            return isBefore(new Date(), UTCToDate(parseISO(offer.expirationTime)))
        })
        if (_getBasketItem(basket, offerKey).offerKey) return
        const product = simpleTourRuntimeStore.offers.find(
            (product: {offers: any[]}) => product.offers.findIndex(offer => offer.offerKey === offerKey) !== -1
        )
        basket.value.push({
            offerKey,
            product,
            info,
            searchRequest: searchRequest.value,
        })
    }

    function REFRESH_BASKET_PRICE({offerKey, prepareBookResponse}: any) {
        const basketItem = _getBasketItem(basket, offerKey)
        if (!basketItem.offerKey) return
        const offer = basketItem.product.offers.find((offer: {offerKey: any}) => offer.offerKey === offerKey)
        const {price} = salesTermsRsTransformer(prepareBookResponse.currentSalesTerms)
        // TODO [P0] Need to check
        offer.currentPrice = price
    }

    function SET_SEARCH_REQUEST(rq: any) {
        searchRequest.value = rq
    }

    function SET_SEARCH_RESPONSE(rs: any) {
        searchResponse.value = rs
    }

    function SET_FILTERS(val: any) {
        filters.value = val
    }

    function SET_FILTER(data: any) {
        filters.value[data.key] = data.value
    }

    function RESET_FILTERS() {
        filters.value = blankFilters()
    }

    function SET_SORT(sort: any) {
        sortFnName.value = sort
    }

    function SET_GROUP_OFFERS_BY(val: any) {
        groupOffersBy.value = val
    }

    function SET_SEARCH_EXPIRATION_TIME(date: any) {
        searchExpirationTime.value = date
    }

    function SET_PREPARE_BOOK_REQUEST(_prepareBookRequest: any) {
        prepareBookRequest.value = _prepareBookRequest
    }

    function SET_PREPARE_BOOK_REQUEST_PROP(data: any) {
        prepareBookRequest.value[data.prop] = data.val
    }

    function SET_PREPARE_BOOK_RESPONSE(_prepareBookResponse: any) {
        prepareBookResponse.value = _prepareBookResponse
    }

    function NEW_SEARCH(_searchRequest: any) {
        searchRequest.value = _searchRequest
        filters.value = blankFilters()
    }

    function RESET() {
        searchRequest.value = blankSearchRequest()
        prepareBookRequest.value = {}
    }

    async function prepareBook(rq: any) {
        runtimeStore.SET_BOOKING_ACTIVE(true)
        try {
            SET_PREPARE_BOOK_REQUEST(rq)
            const rs = await appInstance.$api.prepareBookTour.post(rq)
            SET_PREPARE_BOOK_RESPONSE(rs)
            return rs
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    const isOffersExpired = computed(
        () => () =>
            hasOffers.value && !simpleTourRuntimeStore.searchActive && isAfter(new Date(), searchExpirationTime.value)
    )

    const basketItem = computed(() => (offerKey: any) => _getBasketItem(basket, offerKey))

    const hasOffers = computed(() => !!searchResponse.value?.product?.length)

    function SET_BOOKING_ADDITIONAL_OPTIONS(options: any) {
        if (Object.keys(options).length) {
            bookingAdditionalOptions.value = options
        }
    }

    function clearBookingAdditionalOptions() {
        bookingAdditionalOptions.value = {}
    }

    async function getExtraServiceAdditionalOptions(offerKey: any) {
        try {
            const res = await appInstance.$api.tourAdditionalOptions.get({offerKey})
            SET_BOOKING_ADDITIONAL_OPTIONS(res)
        } catch (error) {
            console.error(error)
        }
    }

    function SET_SORT_FN_NAME(sort: any) {
        sortFnName.value = sort
    }

    return {
        ...useProductStore(searchResponse),
        searchExpirationTime,
        searchRequest,
        searchResponse,
        sortFnName,
        filters,
        prepareBookRequest,
        prepareBookResponse,
        basket,
        basketItem,
        isOffersExpired,
        hasOffers,
        groupOffersBy,
        bookingAdditionalOptions,
        ADD_TO_BASKET,
        REFRESH_BASKET_PRICE,
        SET_SEARCH_REQUEST,
        SET_SEARCH_RESPONSE,
        SET_FILTERS,
        SET_FILTER,
        RESET_FILTERS,
        SET_SORT,
        SET_GROUP_OFFERS_BY,
        SET_SEARCH_EXPIRATION_TIME,
        SET_PREPARE_BOOK_REQUEST,
        SET_PREPARE_BOOK_REQUEST_PROP,
        SET_PREPARE_BOOK_RESPONSE,
        NEW_SEARCH,
        RESET,
        SET_BOOKING_ADDITIONAL_OPTIONS,
        getExtraServiceAdditionalOptions,
        clearBookingAdditionalOptions,
        prepareBook,
        SET_SORT_FN_NAME,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useSimpleTourStore, import.meta.hot))
